<template>
  <div class="text-center">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      location="end"
      :activator="activator"
      persistent
    >
      <v-card min-width="600" class="rounded-lg">
        <rank-table ref="rankTableRef" v-model:items="localItems" :headers="headers" />
        <v-dialog v-model="showDialog" max-width="400">
          <v-card class="px-6 py-8">
            <v-card-title>Add New Item</v-card-title>
            <v-form v-model="valid">
              <v-text-field v-model="newItem.name" label="Name" :rules="[required]" />
              <v-text-field v-model="newItem.url" label="Url" :rules="[required]" />
              <v-text-field v-model="newItem.price" label="Price" :rules="[required, isIntiger]" />
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="showDialog = false">Cancel</v-btn>
                <v-btn color="primary" @click="addItem" :disabled="!valid">Add</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-card-actions>
          <v-btn color="primary" @click="resetNewItem" :disabled="localItems.length >= 5">Add New Item</v-btn>
          <v-spacer />
          <v-btn
            variant="text"
            @click="resetItems"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="saveItems"
            :disabled="!hasChanges"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script setup>
import {
  computed, reactive, ref, watch,
} from 'vue';
import RankTable from './RankTable.vue';

const menu = defineModel('menu');

const props = defineProps({
  activator: { required: true },
  items: { required: true },
});

const localItems = ref(props.items.map((item) => ({ ...item })));
const valid = ref(false);
const required = (value) => !!value || 'This field is required';
const isIntiger = (value) => !value || /^\d+$/.test(String(value)) || 'Must be an integer';

const headers = [
  { title: '', key: 'icon', align: 'right' },
  { title: 'Rank', key: 'rank', align: 'right' },
  { title: 'Name', key: 'name', align: 'right' },
  { title: 'Url', key: 'url', align: 'right' },
  { title: 'Price', key: 'price', align: 'right' },
  { title: 'Actions', key: 'actions', align: 'right' },
];

const showDialog = ref(false);
const rankTableRef = ref(null);

const emit = defineEmits(['save']);

const newItem = reactive({
  rank: null,
  name: '',
  url: '',
  price: '',
});

function resetNewItem() {
  newItem.name = '';
  newItem.url = '';
  newItem.price = '';
  showDialog.value = true;
}

function addItem() {
  const maxRank = localItems.value.length
    ? Math.max(...localItems.value.map((i) => i.rank))
    : 0;
  localItems.value.push({
    rank: maxRank + 1,
    name: newItem.name,
    url: newItem.url,
    price: newItem.price,
  });
  showDialog.value = false;
}

function resetItems() {
  localItems.value = props.items.map((item) => ({ ...item }));
  menu.value = false;
}

function saveItems() {
  emit('save', { newItems: newItems.value, updatedItems: updatedItems.value, deletedItems: deletedItems.value });
  resetItems();
}

const updatedItems = computed(() => localItems.value
  .filter((localItem) => {
    const original = props.items.find((o) => o.id === localItem.id);
    if (!original) return false;
    return JSON.stringify(localItem) !== JSON.stringify(original);
  })
  .map((item) => ({ ...item })));

const deletedItems = computed(() => props.items
  .filter((original) => !localItems.value.some((local) => local.id === original.id))
  .map((item) => ({ ...item })));

const newItems = computed(() => localItems.value
  .filter((item) => !item.id)
  .map((item) => ({ ...item })));

const hasChanges = computed(() => {
  if (rankTableRef.value?.anyEditing) return false;
  return (
    newItems.value.length > 0
    || updatedItems.value.length > 0
    || deletedItems.value.length > 0
  );
});

watch(
  () => props.items,
  (items) => {
    localItems.value = items.map((item) => ({ ...item }));
  },
  { deep: true },
);
</script>
